import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { LoginPage } from '@pages/Anonymous/LoginPage';
import { useInitContext } from '@providers/InitProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery } from '@tanstack/react-query';
import PositiveIcon from 'app/components/icons/Positive.svg';
import { createFetchAuthMeQueryFn } from 'app/queries/auth/createFetchAuthMeQueryFn';
import { authKeys } from 'app/queries/auth/authKeys';
import StatusErrorIcon from 'app/components/icons/StatusError.svg';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Wrapper } from './styles';

type TStatus = 'checking' | 'signIn' | 'done' | 'failed';

const AS_ONE_PRODUCT = 'as1_desktop';
const SSO_RETURN_PARAM = 'sso';

const getProductName = (product: string | null) => (product === AS_ONE_PRODUCT ? 'AutoScriptOne' : 'the desktop app');

export const DesktopSignInPage = () => {
  const { call } = useAPI();
  const { isAuthenticated, isInitialized } = useInitContext();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const handoff = urlSearchParams.get('handoff');
  const isSsoReturn = urlSearchParams.get(SSO_RETURN_PARAM) === '1';
  const productName = getProductName(urlSearchParams.get('product'));

  const [status, setStatus] = useState<TStatus>(() => (handoff ? 'checking' : 'failed'));
  const authMeQuery = useQuery({
    queryKey: authKeys.desktopMe(),
    queryFn: createFetchAuthMeQueryFn(call, { dontCatchErrors: true }),
    enabled: Boolean(handoff && isInitialized && status === 'checking' && !isSsoReturn && isAuthenticated),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const hasFreshAccount = authMeQuery.isFetchedAfterMount && !authMeQuery.isFetching;
  const freshEmail = hasFreshAccount && !authMeQuery.isError ? authMeQuery.data?.email : undefined;
  const browserEmail = typeof freshEmail === 'string' ? freshEmail : '';

  useEffect(() => {
    if (!handoff || !isInitialized || status !== 'checking') {
      return;
    }

    if (isSsoReturn) {
      setStatus('done');
      return;
    }

    if (!isAuthenticated || hasFreshAccount) {
      setStatus('signIn');
    }
  }, [handoff, hasFreshAccount, isAuthenticated, isInitialized, isSsoReturn, status]);

  const ssoReturnUrl = useMemo(() => {
    const params = new URLSearchParams(search);
    params.set(SSO_RETURN_PARAM, '1');

    return `${routes.desktopSignin.make()}?${params.toString()}`;
  }, [search]);

  if (status === 'checking') {
    return <AbsoluteSpinner />;
  }

  if (status === 'signIn' && handoff) {
    return (
      <LoginPage
        desktop={{ handoff, email: browserEmail, returnUrl: ssoReturnUrl }}
        onSignedIn={() => setStatus('done')}
      />
    );
  }

  if (status === 'done') {
    return (
      <Wrapper>
        <PositiveIcon className="icon" />
        <h1 className="heading">You are signed in</h1>
        <p className="message">{productName} has finished signing you in on this computer.</p>
        <p className="message">You can close this tab and return to the app.</p>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <StatusErrorIcon className="icon" />
      <h1 className="heading">Sign-in could not be completed</h1>
      <p className="message">We could not hand your session to {productName}.</p>
      <p className="message">Close this tab and start signing in again from {productName}.</p>
    </Wrapper>
  );
};
